import { TFunction } from 'i18next';
import { Map } from '../definitions/types';
import {
  PACKAGE_TYPE_FREE,
  PACKAGE_TYPE_PRO,
  PACKAGE_TYPE_BUSINESS,
  PACKAGE_TYPE_SHOP,
  PACKAGE_TYPE_PLATIN,
  PACKAGE_TYPE_SEO,
} from './constants';
import { PACKAGE_TYPES } from '../redux/reducers/pricing';
import { isGermanLanguage } from './helpers';

export type Feature = string | ComplexFeature;

export interface ComplexFeature {
  title: string;
  features: string[];
}

export interface PackageDescription {
  name: string;
  description: string;
  subline: string;
  isBestseller: boolean;
  features: Feature[];
}

export function isComplexFeature(feature: Feature): feature is ComplexFeature {
  return typeof feature === 'object';
}

export function isValidPackageType(packageType: string): boolean {
  return PACKAGE_TYPES.indexOf(packageType) !== -1;
}

function getFreePackage(t: TFunction): Map<PackageDescription> {
  return {
    [PACKAGE_TYPE_FREE]: {
      name: t('signup-flow.pricing.free-name'),
      description: t('signup-flow.pricing.free-description'),
      subline: t('signup-flow.pricing.free-subline'),
      isBestseller: false,
      features: [
        t('signup-flow.pricing.feature.functionality'),
        t('signup-flow.pricing.feature.free-subdomain'),
        t('signup-flow.pricing.feature.storage.free-text'),
        t('signup-flow.pricing.feature.https'),
      ],
    },
  };
}

function getProPackage(t: TFunction): Map<PackageDescription> {
  return {
    [PACKAGE_TYPE_PRO]: {
      name: t('signup-flow.pricing.pro-name'),
      description: t('signup-flow.pricing.pro-description'),
      subline: t('signup-flow.pricing.pro-subline'),
      isBestseller: false,
      features: [
        t('signup-flow.pricing.feature.functionality'),
        t('signup-flow.pricing.feature.domain.first.year'),
        t('signup-flow.pricing.feature.platin-experiment.storage.pro-text'),
        t('signup-flow.pricing.feature.https'),
        t('signup-flow.pricing.feature.pro-support'),
        t('signup-flow.pricing.feature.no-advertising'),
        t('signup-flow.pricing.feature.optimized-smartphone'),
      ],
    },
  };
}

function getBusinessPackage(t: TFunction): Map<PackageDescription> {
  return {
    [PACKAGE_TYPE_BUSINESS]: {
      name: t('signup-flow.pricing.business-name'),
      description: t('signup-flow.pricing.business-description'),
      subline: t('signup-flow.pricing.business-subline'),
      isBestseller: true,
      features: [
        t('signup-flow.pricing.feature.functionality'),
        t('signup-flow.pricing.feature.domain.first.year'),
        t('signup-flow.pricing.feature.platin-experiment.storage.business-text'),
        t('signup-flow.pricing.feature.https'),
        t('signup-flow.pricing.feature.g5.business-support'),
        t('signup-flow.pricing.feature.no-advertising'),
        t('signup-flow.pricing.feature.optimized-smartphone'),
        t('signup-flow.pricing.feature.seo'),
        t('signup-flow.pricing.feature.g5.shop.platin'),
      ],
    },
  };
}

function getShopPackage(t: TFunction): Map<PackageDescription> {
  return {
    [PACKAGE_TYPE_SHOP]: {
      name: 'Shop',
      description: 'Unsere Komplett-Lösung für Online-Shop-Besitzer',
      subline: t('signup-flow.pricing.business-subline'),
      isBestseller: false,
      features: [
        t('signup-flow.pricing.feature.functionality'),
        t('signup-flow.pricing.feature.domain.first.year'),
        t('signup-flow.pricing.feature.platin-experiment.storage.business-text'),
        t('signup-flow.pricing.feature.https'),
        t('signup-flow.pricing.feature.g5.business-support'),
        t('signup-flow.pricing.feature.no-advertising'),
        t('signup-flow.pricing.feature.optimized-smartphone'),
        t('signup-flow.pricing.feature.seo'),
        t('signup-flow.pricing.feature.g5.shop.platin'),
        {
          title: 'Alle Shop-Zahlungsoptionen',
          features: ['im Wert von 60 Euro'],
        },
      ],
    },
  };
}

function getPlatinPackage(t: TFunction, hasRankingCoach: boolean): Map<PackageDescription> {
  return {
    [PACKAGE_TYPE_PLATIN]: {
      name: t('signup-flow.pricing.platinum-name'),
      description: t('signup-flow.pricing.platin-description'),
      subline: t('signup-flow.pricing.business-subline'),
      isBestseller: false,
      features: [
        t('signup-flow.pricing.feature.functionality'),
        t('signup-flow.pricing.feature.domain.first.year'),
        t('signup-flow.pricing.feature.platin-experiment.storage.business-text'),
        t('signup-flow.pricing.feature.https'),
        t('signup-flow.pricing.feature.g5.business-support'),
        t('signup-flow.pricing.feature.no-advertising'),
        t('signup-flow.pricing.feature.optimized-smartphone'),
        t('signup-flow.pricing.feature.seo'),
        t('signup-flow.pricing.feature.g5.shop.platin'),
        hasRankingCoach
          ? t('signup-flow.pricing.feature.ranking-coach')
          : `${t('signup-flow.pricing.feature.ranking-coach')}**`,
        t('signup-flow.pricing.feature.platin.expert-tips'),
      ],
    },
  };
}

function getSeoPackage(t: TFunction): Map<PackageDescription> {
  return {
    [PACKAGE_TYPE_SEO]: {
      name: t('signup-flow.pricing.seo-name'),
      description: t('signup-flow.pricing.seo-description'),
      subline: t('signup-flow.pricing.business-subline'),
      isBestseller: false,
      features: [
        t('signup-flow.pricing.feature.functionality'),
        t('signup-flow.pricing.feature.domain.first.year'),
        t('signup-flow.pricing.feature.platin-experiment.storage.business-text'),
        t('signup-flow.pricing.feature.https'),
        t('signup-flow.pricing.feature.g5.business-support'),
        t('signup-flow.pricing.feature.no-advertising'),
        t('signup-flow.pricing.feature.optimized-smartphone'),
        t('signup-flow.pricing.feature.seo'),
        t('signup-flow.pricing.feature.g5.shop.platin'),
        t('signup-flow.pricing.feature.ranking-coach'),
      ],
    },
  };
}

function getFivePackagesWithSeoDesc(t: TFunction): Map<PackageDescription> {
  return Object.assign(
    {},
    getPlatinPackage(t, true),
    getSeoPackage(t),
    getBusinessPackage(t),
    getProPackage(t),
    getFreePackage(t)
  );
}
function getFivePackagesDesc(t: TFunction, hasRankingCoach: boolean): Map<PackageDescription> {
  return Object.assign(
    {},
    getPlatinPackage(t, hasRankingCoach),
    getShopPackage(t),
    getBusinessPackage(t),
    getProPackage(t),
    getFreePackage(t)
  );
}

function getThreePackagesDesc(t: TFunction): Map<PackageDescription> {
  return Object.assign({}, getBusinessPackage(t), getProPackage(t), getFreePackage(t));
}

function appendExtraFeature(
  extraFeature: string | ComplexFeature,
  featureIndex: number,
  features: Feature[]
): Feature[] {
  if (featureIndex >= features.length) {
    features.push(extraFeature);
  } else {
    features.splice(featureIndex, 0, extraFeature);
  }

  return features;
}

function addLanguageSpecificPackages(
  t: TFunction,
  packages: Map<PackageDescription>,
  language: string | null
): Map<PackageDescription> {
  if (language === 'ja') {
    packages.business.features = appendExtraFeature(
      t('signup-flow.pricing.feature.business.jp-fonts.label'),
      9,
      packages.business.features
    );

    packages.pro.features = appendExtraFeature(
      t('signup-flow.pricing.feature.business.jp-fonts.label'),
      packages.pro.features.length,
      packages.pro.features
    );
  }

  if (!isGermanLanguage(language) && Object.keys(packages).length === 3) {
    packages.business.isBestseller = false;
    packages.pro.isBestseller = true;
  }

  if (language === 'de') {
    packages.platin.features = appendExtraFeature(
      {
        title: 'Alle Shop-Zahlungsoptionen',
        features: ['im Wert von 60 Euro'],
      },
      packages.platin.features.length - 1,
      packages.platin.features
    );
  }

  return packages;
}

export default async function getPackages(
  t: TFunction,
  hasFivePackages: boolean = false,
  hasRankingCoach: boolean = false,
  language: string | null
): Promise<Map<PackageDescription>> {
  if (isGermanLanguage(language)) {
    const packages = getFivePackagesDesc(t, hasRankingCoach);
    return addLanguageSpecificPackages(t, packages, language);
  }

  if (hasFivePackages) {
    try {
      const packages = getFivePackagesWithSeoDesc(t);
      return addLanguageSpecificPackages(t, packages, language);
    } catch (e) {
      const packages = getThreePackagesDesc(t);
      return addLanguageSpecificPackages(t, packages, language);
    }
  } else {
    const packages = getThreePackagesDesc(t);
    return addLanguageSpecificPackages(t, packages, language);
  }
}
